import React, { FormEvent, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import * as stripeJs from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { ErrorOutlineOutlined } from "@mui/icons-material";
import { useAuthSelectors } from "redux/selectors";
import PurchaseService from "services/purchase";
import { Button, SubmitButton } from "../../Shared/Button";
import { cards } from "assets/images";
import "./styles.scss";
import { IPointsPlan } from "redux/reducers";

interface CheckoutFormProps {
  pointsPlanToPurchase: IPointsPlan | null;
  onSuccessfulCheckout: () => void;
  handleClose: () => void;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  pointsPlanToPurchase,
  onSuccessfulCheckout,
  handleClose,
}) => {
  const [isProcessing, setProcessingTo] = useState<boolean>(false);
  const [checkoutError, setCheckoutError] = useState<string | undefined>("");
  const { selectAuthUser } = useAuthSelectors();
  const [isCompleteCardDetail, setIsCompleteCardDetail] =
    useState<boolean>(false);
  const stripe = useStripe();
  const { t, i18n } = useTranslation();
  const elements = useElements();

  const handleCardDetailsChange = (
    ev: stripeJs.StripeCardElementChangeEvent
  ) => {
    ev.error ? setCheckoutError(ev.error.message) : setCheckoutError("");
    setIsCompleteCardDetail(ev.complete);
  };

  const cardElementOpts: stripeJs.StripeCardElementOptions = {
    iconStyle: "solid",
    hidePostalCode: true,
  };

  const handleFormSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!elements || !stripe) {
      return;
    }
    setProcessingTo(true);

    const cardElement = elements.getElement("card");
    if (!cardElement) {
      setProcessingTo(false);
      return;
    }
    if (!isCompleteCardDetail) {
      setCheckoutError("Please enter your card details");
      setProcessingTo(false);
      return;
    }

    try {
      if (!pointsPlanToPurchase || !selectAuthUser) {
        setProcessingTo(false);
        return;
      }
      const { data: clientSecret } = await PurchaseService.getPaymentIntent({
        points_plan_id: pointsPlanToPurchase.id,
      });

      const paymentMethodReq = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        metadata: {
          number_of_points: pointsPlanToPurchase.amount,
          tax_value: pointsPlanToPurchase.tax,
        },
      });

      if (paymentMethodReq.error) {
        setCheckoutError(paymentMethodReq.error.message);
        setProcessingTo(false);
        return;
      }

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodReq.paymentMethod.id,
      });

      if (error) {
        setCheckoutError(error.message);
        setProcessingTo(false);
        return;
      }
      onSuccessfulCheckout();
    } catch (err) {
      setCheckoutError("Unable to complete your purchase.Try again");
      setProcessingTo(false);
    }
  };

  return (
    <div className="checkout_form">
      <form onSubmit={handleFormSubmit}>
        <div className="col2-area">
          <div className="form-area">
            <div className="error_message">
              {checkoutError}
              {checkoutError && <ErrorOutlineOutlined />}
            </div>
            <div className="card_element_container">
              <CardElement
                options={cardElementOpts}
                onChange={handleCardDetailsChange}
              />
            </div>
          </div>
          <div className="img-area">
            <img src={cards} alt="" />
          </div>
        </div>
        <p className="para">{t("purchasePoints.checkoutForm.header")}</p>
        <div className="purchase_submit_button_row">
          <Button
            customStyles="gray"
            variant="outlined"
            label={t("purchasePoints.label.doNotSend")}
            onClick={handleClose}
          />
          <SubmitButton
            disabled={
              isProcessing ||
              !stripe ||
              !elements ||
              !pointsPlanToPurchase ||
              !isCompleteCardDetail
            }
            customStyles={
              isProcessing ||
              !stripe ||
              !elements ||
              !pointsPlanToPurchase ||
              !isCompleteCardDetail
                ? "disabled"
                : "magenta"
            }
            label={t("purchasePoints.label.send")}
          />
        </div>
      </form>
    </div>
  );
};

export default CheckoutForm;
